import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Select, Tag, Button, Modal, Form,
  message, Card, Row, Col, Typography, Input, Space, Popover, Popconfirm,
} from 'antd';
import { CheckOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { canEdit } from '../utils/auth';
import { TASK_STATUS_COLORS, palette } from '../theme';
import Pill from '../components/Pill';

const TASK_TYPE_LABELS: Record<string, string> = {
  duplicate_check: 'Проверка дубля',
  inn_not_found: 'Не найден ИНН',
  data_mismatch: 'Расхождение данных',
};

const isResolvable = (rec: any) => rec.status !== 'resolved' && rec.status !== 'dismissed';

const SUGGESTIONS_MARKER = 'Возможные совпадения по названию';

const splitSuggestions = (description: string): { main: string; suggestions: string | null } => {
  const idx = description.indexOf(SUGGESTIONS_MARKER);
  if (idx === -1) return { main: description, suggestions: null };
  return { main: description.slice(0, idx).trim(), suggestions: description.slice(idx) };
};

// Задачи на проверку дубля (duplicate_check) ссылаются на совпавшую запись
// в тексте описания как "ID: 570" (иногда список "ID: 8611, 8613, 8614") или
// "ID 97:" (перед перечислением расхождений по полям для manual_review).
// Реальный случай: чтобы понять, какие ДВЕ карточки сравниваются, раньше
// приходилось руками копировать число из текста и вставлять в поиск —
// делаем упомянутые ID кликабельными ссылками сразу в тексте описания,
// как уже сделано для колонки "ЮЛ".
const ID_REFERENCE_REGEX = /\bID:?\s*(\d+(?:\s*,\s*\d+)*)/g;

// Реальный случай: раньше решить задачу "Проверка дубля" означало 5 шагов
// (открыть задачу → кликнуть на компанию → перейти на страницу ЮЛ →
// удалить дубль → вернуться на задачи → снова отфильтровать) — теперь
// прямо тут же, рядом с каждой упомянутой записью, кнопка "Удалить" с
// подтверждением; после удаления backend сам закрывает задачу (см.
// LegalEntityController.autoResolveOpenTasks), список задач просто
// обновляется.
const renderDescriptionWithEntityLinks = (
  text: string,
  onOpenCompare: (id: number) => void,
  editable: boolean,
  onDelete: (id: number) => void,
): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  ID_REFERENCE_REGEX.lastIndex = 0;
  while ((match = ID_REFERENCE_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const prefix = match[0].slice(0, match[0].indexOf(match[1]));
    nodes.push(prefix);
    const ids = match[1].split(',').map(s => s.trim());
    ids.forEach((id, i) => {
      if (i > 0) nodes.push(', ');
      nodes.push(
        <Typography.Link key={`id-${nodes.length}`} onClick={() => onOpenCompare(parseInt(id, 10))}>
          {id}
        </Typography.Link>,
      );
      if (editable) {
        nodes.push(
          <Popconfirm
            key={`del-${nodes.length}`}
            title={`Удалить ЮЛ ID ${id} как дубль?`}
            onConfirm={() => onDelete(parseInt(id, 10))}
            okText="Удалить" cancelText="Отмена"
          >
            <DeleteOutlined style={{ marginLeft: 4, color: palette.red, cursor: 'pointer' }} />
          </Popconfirm>,
        );
      }
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

// Реальный случай: описание задачи "Проверка дубля" может ссылаться сразу
// на НЕСКОЛЬКО потенциально совпадающих записей ("ID: 8611, 8613, 8614") —
// проверяющему нужно видеть их ВСЕ разом рядом с текущей записью, чтобы
// сравнить, а не открывать по одной. Собираем ID текущей записи (у которой
// открыта задача) плюс все ID, упомянутые в описании, без повторов.
const extractAllReferencedIds = (text: string, ownId?: number): number[] => {
  const ids = new Set<number>();
  if (ownId) ids.add(ownId);
  let match: RegExpExecArray | null;
  const regex = new RegExp(ID_REFERENCE_REGEX);
  regex.lastIndex = 0;
  while ((match = regex.exec(text)) !== null) {
    match[1].split(',').forEach(s => {
      const n = parseInt(s.trim(), 10);
      if (!isNaN(n)) ids.add(n);
    });
  }
  return Array.from(ids);
};

const ReviewTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | undefined>('open');
  const [taskType, setTaskType] = useState<string | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [resolveTarget, setResolveTarget] = useState<{ ids: number[]; label: string; description?: string } | null>(null);
  const [compareIds, setCompareIds] = useState<number[] | null>(null);
  const [compareData, setCompareData] = useState<any[]>([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const editable = canEdit();

  const fetchData = useCallback(() => {
    setLoading(true);
    const params: any = {};
    if (status) params.status = status;
    if (taskType) params.task_type = taskType;
    api.get('/tasks/', { params })
      .then(r => { setTasks(r.data); setSelectedIds([]); setCurrentPage(1); })
      .catch(() => message.error('Ошибка загрузки задач'))
      .finally(() => setLoading(false));
  }, [status, taskType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Реальный случай: раньше решение задачи "Проверка дубля" требовало уйти
  // со страницы задач на страницу ЮЛ и обратно — теперь удаление доступно
  // прямо тут же (см. renderDescriptionWithEntityLinks). Backend сам
  // закрывает связанную задачу при удалении (autoResolveOpenTasks), просто
  // обновляем список.
  const handleDeleteEntity = async (id: number) => {
    try {
      await api.delete(`/entities/${id}`);
      message.success(`ЮЛ ID ${id} удалено`);
      fetchData();
    } catch {
      message.error('Ошибка удаления');
    }
  };

  // Реальный случай (SAMARA HEAT AND ENERGY PROPERTY FUND): "Сравнить все"
  // раньше уводил на страницу ЮЛ (другая вкладка) — чтобы вернуться к
  // остальным задачам, приходилось переключаться обратно вручную. Теперь
  // сравнение и удаление дубля происходят В МОДАЛЬНОМ ОКНЕ прямо на
  // странице "Задачи на проверку" — переключать вкладки не нужно вообще.
  const openCompare = async (ids: number[]) => {
    setCompareIds(ids);
    setCompareLoading(true);
    try {
      const res = await api.get('/entities', { params: { search: ids.join(','), page_size: ids.length } });
      setCompareData(res.data.items || []);
    } catch {
      message.error('Ошибка загрузки записей для сравнения');
      setCompareData([]);
    } finally {
      setCompareLoading(false);
    }
  };

  const handleDeleteInCompare = async (id: number) => {
    try {
      await api.delete(`/entities/${id}`);
      message.success(`ЮЛ ID ${id} удалено`);
      setCompareData(prev => prev.filter(e => e.id !== id));
      fetchData();
    } catch {
      message.error('Ошибка удаления');
    }
  };

  const handleResolve = async (values: { status: string; resolution: string }) => {
    if (!resolveTarget) return;
    try {
      await api.post('/tasks/bulk-resolve', { task_ids: resolveTarget.ids, ...values });
      message.success(resolveTarget.ids.length > 1 ? `Обновлено задач: ${resolveTarget.ids.length}` : 'Задача обновлена');
      setResolveTarget(null);
      fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const columns: any[] = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: 'Тип', dataIndex: 'task_type', width: 160,
      render: (t: string) => TASK_TYPE_LABELS[t] || t,
    },
    {
      title: 'ЮЛ', key: 'entity', width: 220,
      render: (_: any, rec: any) => rec.legal_entity_id ? (
        <Typography.Link onClick={() => navigate(`/entities?search=${encodeURIComponent(rec.entity_inn || rec.entity_name || '')}`)}>
          {rec.entity_name || `ID ${rec.legal_entity_id}`}
        </Typography.Link>
      ) : '—',
    },
    {
      title: 'Описание', dataIndex: 'description', width: 360,
      render: (desc: string, rec: any) => {
        if (!desc) return '—';
        const { main, suggestions } = splitSuggestions(desc);
        // Реальный случай: описание задачи "Проверка дубля" ссылается на
        // совпавшую запись (иногда сразу НЕСКОЛЬКО) по ID в тексте ("ID:
        // 8611, 8613, 8614") — чтобы понять, какие карточки сравниваются, а
        // не гадать/копировать числа вручную, показываем полный текст с
        // кликабельными ID-шками во всплывающем окне поверх усечённого
        // превью, плюс одну кнопку "Сравнить все" — она открывает
        // ВСЕ потенциальные дубли (включая саму текущую запись) в модальном
        // окне ПРЯМО ТУТ (см. openCompare), без перехода на вкладку ЮЛ.
        const hasIdReference = /\bID:?\s*\d+/.test(main);
        const allIds = hasIdReference ? extractAllReferencedIds(main, rec.legal_entity_id) : [];
        return (
          <Space direction="vertical" size={4} style={{ maxWidth: 340 }}>
            {hasIdReference ? (
              <Popover
                title="Сравнить записи"
                content={(
                  <div style={{ maxWidth: 420 }}>
                    {allIds.length > 1 && (
                      <Button
                        size="small" type="primary" style={{ marginBottom: 8 }}
                        onClick={() => openCompare(allIds)}
                      >
                        Сравнить все ({allIds.length})
                      </Button>
                    )}
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {renderDescriptionWithEntityLinks(main, (id) => openCompare([id]), editable, handleDeleteEntity)}
                    </div>
                  </div>
                )}
                trigger="click"
              >
                <Typography.Text ellipsis={{ tooltip: false }} style={{ maxWidth: 340, cursor: 'pointer' }} underline>
                  {main}
                </Typography.Text>
              </Popover>
            ) : (
              <Typography.Text ellipsis={{ tooltip: main }} style={{ maxWidth: 340 }}>
                {main}
              </Typography.Text>
            )}
            {suggestions && (
              <Popover
                title="Варианты по названию (Dadata)"
                content={<div style={{ whiteSpace: 'pre-line', maxWidth: 420 }}>{suggestions}</div>}
                trigger="click"
              >
                <Tag icon={<SearchOutlined />} color="blue" style={{ cursor: 'pointer' }}>
                  Есть варианты по названию
                </Tag>
              </Popover>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Статус', dataIndex: 'status', width: 120,
      render: (s: string) => {
        const info = TASK_STATUS_COLORS[s] || { bg: palette.border, fg: palette.muted, label: s };
        return <Pill bg={info.bg} fg={info.fg}>{info.label}</Pill>;
      },
    },
    {
      title: 'Создана', dataIndex: 'created_at', width: 160,
      render: (v: string) => v ? new Date(v).toLocaleString('ru-RU') : '—',
    },
    ...(editable ? [{
      title: 'Действия', key: 'actions', width: 100,
      render: (_: any, rec: any) => (
        <Button size="small" icon={<CheckOutlined />}
          disabled={!isResolvable(rec)}
          onClick={() => setResolveTarget({ ids: [rec.id], label: `задачу #${rec.id}`, description: rec.description })}>
          Решить
        </Button>
      ),
    }] : []),
  ];

  return (
    <div>
      <div className="eyebrow">Compliance-очередь</div>
      <Typography.Title level={3} className="page-title" style={{ marginTop: 0 }}>Задачи на проверку</Typography.Title>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle" justify="space-between">
          <Col>
            <Space>
              <Select placeholder="Статус" allowClear style={{ width: 160 }}
                value={status}
                onChange={setStatus}
                options={[
                  { label: 'Открыта', value: 'open' },
                  { label: 'В работе', value: 'in_progress' },
                  { label: 'Решена', value: 'resolved' },
                  { label: 'Отклонена', value: 'dismissed' },
                ]} />
              <Select placeholder="Тип задачи" allowClear style={{ width: 200 }}
                value={taskType}
                onChange={setTaskType}
                options={Object.entries(TASK_TYPE_LABELS).map(([value, label]) => ({ value, label }))} />
            </Space>
          </Col>
          {editable && selectedIds.length > 0 && (
            <Col>
              <Button type="primary" icon={<CheckOutlined />}
                onClick={() => setResolveTarget({ ids: selectedIds as number[], label: `выбранные задачи (${selectedIds.length})` })}>
                Решить выбранные ({selectedIds.length})
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={tasks}
        rowKey="id"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          onChange: (page, size) => {
            setCurrentPage(page);
            if (size !== pageSize) setPageSize(size);
          },
        }}
        size="small"
        rowSelection={editable ? {
          selectedRowKeys: selectedIds,
          onChange: setSelectedIds,
          getCheckboxProps: (rec: any) => ({ disabled: !isResolvable(rec) }),
        } : undefined}
      />

      <Modal
        key={resolveTarget?.ids.join(',') || 'none'}
        title={`Решить ${resolveTarget?.label ?? ''}`}
        open={!!resolveTarget}
        onCancel={() => setResolveTarget(null)}
        footer={null}
      >
        {resolveTarget?.description && (
          <div style={{
            whiteSpace: 'pre-line', marginBottom: 16, padding: 12,
            background: '#FBF9F3', borderRadius: 6, fontSize: 13,
          }}>
            {resolveTarget.description}
          </div>
        )}
        <Form onFinish={handleResolve} layout="vertical"
          initialValues={{ status: 'resolved', resolution: '' }}>
          <Form.Item name="status" label="Новый статус" rules={[{ required: true }]}>
            <Select options={[
              { label: 'В работе', value: 'in_progress' },
              { label: 'Решена', value: 'resolved' },
              { label: 'Отклонена', value: 'dismissed' },
            ]} />
          </Form.Item>
          <Form.Item name="resolution" label="Комментарий / решение">
            <Input.TextArea rows={3} placeholder="Что было сделано / почему отклонена" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>Сохранить</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Реальный случай (SAMARA HEAT AND ENERGY PROPERTY FUND): раньше
          "Сравнить все" уводил на страницу ЮЛ — чтобы вернуться к
          остальным задачам, приходилось переключаться между вкладками
          вручную. Таблица-сравнение и удаление дубля — прямо здесь, без
          ухода со страницы "Задачи на проверку". */}
      <Modal
        title={`Сравнение записей${compareIds ? ` (${compareIds.length})` : ''}`}
        open={!!compareIds}
        onCancel={() => setCompareIds(null)}
        footer={null}
        width={720}
      >
        <Table
          size="small"
          loading={compareLoading}
          pagination={false}
          rowKey={(r: any) => r.field}
          columns={[
            { title: '', dataIndex: 'field', width: 140, fixed: 'left' as const },
            ...compareData.map((e: any) => ({
              title: (
                <Space direction="vertical" size={0}>
                  <span>ID {e.id}</span>
                  {editable && (
                    <Popconfirm
                      title={`Удалить ЮЛ ID ${e.id}?`}
                      onConfirm={() => handleDeleteInCompare(e.id)}
                      okText="Удалить" cancelText="Отмена"
                    >
                      <Button size="small" danger icon={<DeleteOutlined />}>Удалить</Button>
                    </Popconfirm>
                  )}
                </Space>
              ),
              dataIndex: e.id.toString(),
              width: 220,
            })),
          ]}
          dataSource={[
            { field: 'Название (рус.)', key: 'name_ru' },
            { field: 'Название (англ.)', key: 'name_en' },
            { field: 'ИНН', key: 'inn' },
            { field: 'ОГРН', key: 'ogrn' },
            { field: 'Юрисдикция', key: 'jurisdiction' },
            { field: 'Адрес', key: 'address' },
            { field: 'Программа', key: 'sanction_program' },
            { field: 'Номер списка', key: 'list_number' },
            { field: 'Статус', key: 'status' },
          ].map(row => {
            const rec: any = { field: row.field };
            compareData.forEach((e: any) => { rec[e.id.toString()] = e[row.key] || '—'; });
            return rec;
          })}
          scroll={{ x: true }}
        />
      </Modal>
    </div>
  );
};

export default ReviewTasksPage;
